import { useQuery } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { logger } from '@/lib/logger';

import type { ReleaseNote } from '@/lib/microcms/client';

const STORAGE_KEY = 'kakusill:lastSeenReleaseNoteId';
const DIALOG_DISMISSED_KEY = 'kakusill:lastDismissedDialogNoteId';
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
type Listener = () => void;

interface ReleaseNotesApiResponse {
  success: boolean;
  data?: ReleaseNote[];
  error?: string;
}

async function fetchReleaseNotes(): Promise<ReleaseNote[]> {
  const response = await fetch('/api/notifications/release-notes');
  if (!response.ok) {
    throw new Error('Failed to fetch release notes');
  }
  const payload: unknown = await response.json();

  // API 契約: { success: true, data: ReleaseNote[] }
  const typedPayload = payload as ReleaseNotesApiResponse;
  if (typedPayload.success && Array.isArray(typedPayload.data)) {
    return typedPayload.data;
  }

  // フォールバック: 直接配列が返る場合（後方互換）
  if (Array.isArray(payload)) {
    return payload as ReleaseNote[];
  }

  logger.warn('[useReleaseNotes] Unexpected response shape:', payload);
  return [];
}

function getServerSnapshot(): string | null {
  return null;
}

function createLocalStorageExternalStore(key: string) {
  const listeners = new Set<Listener>();
  let snapshot: string | null = null;

  function getFromStorage(): string | null {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function setSnapshot(next: string | null): void {
    snapshot = next;
  }

  function getSnapshot(): string | null {
    if (listeners.size === 0) {
      return getFromStorage();
    }
    return snapshot;
  }

  function emitChange(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function subscribe(callback: Listener): () => void {
    setSnapshot(getFromStorage());
    listeners.add(callback);
    if (typeof window === 'undefined') {
      return () => {
        listeners.delete(callback);
      };
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== key) return;
      setSnapshot(event.newValue);
      callback();
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(callback);
      window.removeEventListener('storage', handleStorage);
    };
  }

  function setValue(id: string): void {
    setSnapshot(id);
    if (typeof window === 'undefined') {
      emitChange();
      return;
    }

    try {
      localStorage.setItem(key, id);
    } catch {
      // プライベートブラウジングやクォータ超過時は無視
    } finally {
      emitChange();
    }
  }

  return {
    getSnapshot,
    subscribe,
    setValue,
  };
}

const lastSeenStore = createLocalStorageExternalStore(STORAGE_KEY);
const dialogDismissedStore = createLocalStorageExternalStore(DIALOG_DISMISSED_KEY);

export function useReleaseNotes() {
  const { status: sessionStatus } = useSession();
  const isAuthenticated = sessionStatus === 'authenticated';
  const lastSeenId = useSyncExternalStore(
    lastSeenStore.subscribe,
    lastSeenStore.getSnapshot,
    getServerSnapshot
  );
  const lastDismissedDialogId = useSyncExternalStore(
    dialogDismissedStore.subscribe,
    dialogDismissedStore.getSnapshot,
    getServerSnapshot
  );

  const { data: rawReleaseNotes = [], ...queryResult } = useQuery<ReleaseNote[]>({
    queryKey: ['release-notes'],
    queryFn: fetchReleaseNotes,
    staleTime: STALE_TIME,
    enabled: isAuthenticated,
  });

  // 未認証時はキャッシュデータをマスク
  const releaseNotes = useMemo(
    () => (isAuthenticated ? rawReleaseNotes : []),
    [isAuthenticated, rawReleaseNotes]
  );

  const hasUnread = useMemo(() => {
    if (!isAuthenticated) return false;
    if (releaseNotes.length === 0) return false;
    return releaseNotes[0].id !== lastSeenId;
  }, [isAuthenticated, releaseNotes, lastSeenId]);

  const shouldShowDialog = useMemo(() => {
    if (!hasUnread) return false;
    return releaseNotes[0].id !== lastDismissedDialogId;
  }, [hasUnread, releaseNotes, lastDismissedDialogId]);

  const markAsRead = useCallback(() => {
    if (releaseNotes.length > 0) {
      const latestId = releaseNotes[0].id;
      lastSeenStore.setValue(latestId);
    }
  }, [releaseNotes]);

  const dismissDialog = useCallback(() => {
    if (releaseNotes.length > 0) {
      const latestId = releaseNotes[0].id;
      dialogDismissedStore.setValue(latestId);
    }
  }, [releaseNotes]);

  return {
    releaseNotes,
    hasUnread,
    shouldShowDialog,
    markAsRead,
    dismissDialog,
    ...queryResult,
  };
}
